import { DATE_LIMITS } from '../forms/formBlocks';

/**
 * Validation result interface
 */
export interface ValidationResult {
  isValid: boolean;
  errorMessage: string | null;
}

/**
 * Common validation error messages
 */
export const VALIDATION_ERRORS = {
  REQUIRED: "This field is required.",
  INVALID_DATE: "Please enter a valid date.",
  FUTURE_DATE: "Date of birth cannot be in the future.",
  DATE_TOO_OLD: "Please enter a valid birth date (maximum 120 years ago).",
  INVALID_EMAIL: "Please enter a valid email address.",
  INVALID_PHONE: "Please enter a valid phone number."
} as const;

/**
 * Utility functions for common validation patterns
 */
export const ValidationUtils = {
  /**
   * Check if a value is empty or null/undefined
   */
  isEmpty(value: any): boolean {
    return value === null || value === undefined || (typeof value === 'string' ? value.trim() === '' : value.toString().trim() === '');
  },

  /**
   * Check if a string is a valid email format
   * Uses a more comprehensive regex for email validation.
   */
  isValidEmail(email: string): boolean {
      // RFC 5322 Official Standard regex (simplified for practical use)
      const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
      return emailRegex.test(email);
  },

};

/**
 * Validate a birth date string using DATE_LIMITS from formBlocks.
 * Returns a ValidationResult with isValid and optional errorMessage.
 */
export function validateBirthDate(dateString: string): ValidationResult {
  // Empty value
  if (!dateString || dateString.trim() === '') {
    return { isValid: false, errorMessage: "Please enter a date." };
  }

  // Parse date (QuillForms uses MM/DD/YYYY strings)
  const dateValue = new Date(dateString);
  const today = DATE_LIMITS.newest;
  const oldest = DATE_LIMITS.oldest;

  // Invalid date
  if (isNaN(dateValue.getTime())) {
    return { isValid: false, errorMessage: VALIDATION_ERRORS.INVALID_DATE };
  }

  // Future date
  if (dateValue > today) {
    return { isValid: false, errorMessage: VALIDATION_ERRORS.FUTURE_DATE };
  }

  // Too old
  if (dateValue < oldest) {
    return { isValid: false, errorMessage: VALIDATION_ERRORS.DATE_TOO_OLD };
  }

  return { isValid: true, errorMessage: null };
}

/**
 * Single exported factory that creates a beforeGoingNext handler for QuillForms.
 * This replaces the previous class-based API. The returned handler validates the
 * configured block(s) (currently only birth date validation) and either advances
 * the form or sets field-level validation errors.
 *
 * Usage:
 *   const handler = createBeforeGoingNextValidator('q7');
 *   <Form beforeGoingNext={handler} ... />
 */
export interface BeforeNextContext {
  currentBlockId: string;
  answers: Record<string, { value: any }>;
}

export function createBeforeGoingNextValidator(
  blockId: string,
  onBeforeNext?: (ctx: BeforeNextContext) => void
) {
  return ({
    setIsFieldValid,
    currentBlockId,
    answers,
    setFieldValidationErr,
    setIsCurrentBlockSafeToSwipe,
    goNext
  }: {
    setIsFieldValid: (blockId: string, isValid: boolean) => void;
    currentBlockId: string;
    answers: any;
    setFieldValidationErr: (blockId: string, error: string | null) => void;
    setIsCurrentBlockSafeToSwipe: (isSafe: boolean) => void;
    goNext: () => void;
  }) => {
    // Only validate the configured block
    if (currentBlockId === blockId) {
      const value = answers?.[currentBlockId]?.value;

      // Validate birth date
      const validation = validateBirthDate(value);

      if (validation.isValid) {
        setIsFieldValid(currentBlockId, true);
        setFieldValidationErr(currentBlockId, null);
        setIsCurrentBlockSafeToSwipe(true);
      } else {
        setIsFieldValid(currentBlockId, false);
        setFieldValidationErr(currentBlockId, validation.errorMessage);
        setIsCurrentBlockSafeToSwipe(false);
        return; // prevent advancing
      }
    }

    // Fire the tracking callback before advancing
    onBeforeNext?.({ currentBlockId, answers });

    // If no issues, continue
    goNext();
  };
}